import { useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Clock, MapPin, MonitorSmartphone } from 'lucide-react-native'
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { colors, formatRelativeDateTime } from '@chillout/core'
import { AdminTable } from '../../shared/Table'
import { AdminFilterDropdown, type FilterGroup } from '../../shared/AdminFilterDropdown'
import { AdminPill } from '../../shared/AdminPill'
import { Card } from '../../../shared/Card'
import type { Device } from './useDevices'

interface DevicesSectionProps {
  devices: Device[]
  isLoading: boolean
  isError: boolean
  onRegisterDevice: () => void
  onEditDevice: (device: Device) => void
  onRetry: () => void
}

export function DevicesSection({
  devices,
  isLoading,
  isError,
  onRegisterDevice,
  onEditDevice,
  onRetry,
}: DevicesSectionProps) {
  const { t, i18n } = useTranslation()
  const [activeFilters, setActiveFilters] = useState<Record<string, string | string[] | undefined>>({
    status: ['active', 'pending'],
  })

  const now = useMemo(() => new Date(), [])
  const dateLabels = useMemo(() => ({ today: t('common.today'), yesterday: t('common.yesterday') }), [t])

  const columnFilters = useMemo<ColumnFiltersState>(
    () =>
      Object.entries(activeFilters)
        .filter(([, v]) => v !== undefined)
        .map(([id, value]) => ({ id, value })),
    [activeFilters],
  )

  const branchOptions = useMemo(
    () =>
      [...new Set(devices.map((d) => d.branchName))]
        .filter(Boolean)
        .sort()
        .map((name) => ({ label: name, value: name })),
    [devices],
  )

  const filterGroups = useMemo<FilterGroup[]>(() => {
    const groups: FilterGroup[] = [
      {
        id: 'status',
        label: t('operations.devices.columns.status'),
        multiSelect: true,
        options: [
          { label: t('operations.statuses.active'), value: 'active' },
          { label: t('operations.statuses.inactive'), value: 'inactive' },
          { label: t('operations.statuses.pending'), value: 'pending' },
        ],
      },
    ]
    if (branchOptions.length > 1) {
      groups.push({
        id: 'branch',
        label: t('operations.devices.columns.branch'),
        multiSelect: true,
        options: branchOptions,
      })
    }
    return groups
  }, [t, branchOptions])

  const columns = useMemo<ColumnDef<Device>[]>(
    () => [
      {
        id: 'name',
        header: t('operations.devices.columns.device'),
        size: 220,
        cell: ({ row }) => (
          <Text className="text-sm font-semibold text-primary" numberOfLines={1}>
            {row.original.name}
          </Text>
        ),
      },
      {
        accessorKey: 'branchName',
        id: 'branch',
        header: t('operations.devices.columns.branch'),
        size: 100,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true
          const value = row.getValue<string>(columnId)
          return Array.isArray(filterValue) ? filterValue.includes(value) : value === filterValue
        },
        cell: ({ row }) => (
          <Text className="text-sm font-normal text-primary" numberOfLines={1}>
            {row.original.branchName}
          </Text>
        ),
      },
      {
        accessorKey: 'status',
        header: t('operations.devices.columns.status'),
        size: 128,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true
          const value = row.getValue<string>(columnId)
          return Array.isArray(filterValue) ? filterValue.includes(value) : value === filterValue
        },
        cell: ({ row }) => (
          <View className="items-center">
            <AdminPill label={t(`operations.statuses.${row.original.status}`)} tone={row.original.status} />
          </View>
        ),
        meta: { align: 'center' as const },
      },
      {
        id: 'lastActivity',
        header: t('operations.devices.columns.lastActivity'),
        size: 200,
        cell: ({ row }) => (
          <Text className="text-xs font-normal text-primary" numberOfLines={1}>
            {row.original.lastActivityAt
              ? formatRelativeDateTime(row.original.lastActivityAt, now, dateLabels, i18n.language)
              : '—'}
          </Text>
        ),
      },
    ],
    [t, i18n.language, now, dateLabels],
  )

  const table = useReactTable({
    data: devices,
    columns,
    state: { columnFilters },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const isEmpty = !isLoading && devices.length === 0
  const showFilter = !isError && !isEmpty
  const hasActiveFilters = Object.values(activeFilters).some(Boolean)

  function handleFilterChange(id: string, value: string | string[] | undefined) {
    setActiveFilters((prev) => ({ ...prev, [id]: value }))
  }

  return (
    <Card variant="dashboard" className="p-6">
      <View className="gap-2">
        <View className="flex-row items-center justify-between gap-4">
          <Text className="text-xl font-bold text-primary">{t('operations.devices.title')}</Text>
          {showFilter && (
            <AdminFilterDropdown
              groups={filterGroups}
              activeFilters={activeFilters}
              onChange={handleFilterChange}
              filterLabel={t('operations.filters.label')}
              clearLabel={t('operations.filters.clear')}
              disabled={isLoading}
            />
          )}
        </View>
        <Text className="text-base font-normal text-secondary">{t('operations.devices.description')}</Text>
      </View>
      <AdminTable
        table={table}
        minWidth={648}
        isLoading={isLoading}
        isError={isError}
        hasActiveFilters={hasActiveFilters}
        onRowPress={onEditDevice}
        rowAccessibilityLabel={(device) =>
          t('operations.devices.editAccessibilityLabel', { name: device.name })
        }
        renderMobileRow={(device) => (
          <>
            <Text className="text-sm font-semibold text-primary" numberOfLines={1}>
              {device.name}
            </Text>
            <View className="mt-2 flex-row items-center gap-2">
              <MapPin size={14} color={colors.text.muted} strokeWidth={1.8} />
              <Text className="text-sm font-normal text-secondary" numberOfLines={1}>
                {device.branchName}
              </Text>
              <View className="h-3 w-px bg-border" />
              <AdminPill label={t(`operations.statuses.${device.status}`)} tone={device.status} />
              <View className="h-3 w-px bg-border" />
              <Clock size={14} color={colors.text.muted} strokeWidth={1.8} />
              <Text className="text-sm font-normal text-secondary" numberOfLines={1}>
                {device.lastActivityAt
                  ? formatRelativeDateTime(device.lastActivityAt, now, dateLabels, i18n.language)
                  : '—'}
              </Text>
            </View>
          </>
        )}
        onRetry={onRetry}
        errorMessage={t('operations.devices.loadError')}
        noResultsDescription={t('operations.devices.noResults')}
        emptyIcon={MonitorSmartphone}
        emptyTitle={t('operations.devices.emptyTitle')}
        emptyDescription={t('operations.devices.emptyDescription')}
        emptyButtonLabel={t('operations.actions.registerDevice')}
        onEmptyAction={onRegisterDevice}
        skeletonRowCount={4}
      />
    </Card>
  )
}